<template>
  <div class="club-details grid__row" data-test="club-details">
    <div class="club-details__row-heading grid__column grid__column--12">
      <h2 class="club-details__heading heading--h3">
        {{ heading }}
      </h2>
    </div>
    <div class="grid__column grid__column--12 grid__column--breakpoint--7--5">
      <div class="club-details__header">
        <ImageWrapper class="club-details__avatar" v-bind="avatarProps" />
        <div class="club-details__heading-wrapper">
          <h3 class="club-details__club-name">
            <router-link
              class="club-details__details body--hyperlink--semibold"
              :to="to"
            >
              {{ clubName }}
            </router-link>
          </h3>
          <USRatingStars
            v-if="clubRating > 0"
            :stars="5"
            :filled="clubRating"
            css-diameter="var(--stars-diameter)"
            class="club-details__rating"
          />
        </div>
      </div>
      <div class="club-details__contact body--p1--regular">
        <div
          v-if="clubAddress"
          class="club-details__contact-item club-details__contact-item--address"
        >
          <USIcon
            class="club-details__contact-icon"
            color="var(--color--primary-b1)"
            name="location-pin"
            css-diameter="var(--contact-icon-diameter)"
          />
          <span>
            {{ clubAddress }}
          </span>
        </div>
        <div
          v-if="clubPhoneNumber"
          class="club-details__contact-item club-details__contact-item--phone"
        >
          <USIcon
            class="club-details__contact-icon"
            color="var(--color--primary-b1)"
            name="phone"
            css-diameter="var(--contact-icon-diameter)"
          />
          <a :href="`tel:${clubPhoneNumber}`">
            {{ clubPhoneNumber }}
          </a>
        </div>
      </div>
    </div>
    <div
      v-if="amenities && amenities.length"
      class="club-details__amenities grid__column grid__column--12 grid__column--breakpoint--7--6 grid__column--breakpoint--7--offset--1"
    >
      <USList :columns="2" type="icon" class="club-details__amenity-list">
        <USIconListItem
          v-for="(amenity, idx) in amenities"
          :key="idx"
          class="club-details__amenity"
          :icon="amenity.icon || 'person-bg'"
          css-diameter="var(--amenity-icon-diameter)"
          color="var(--font-color--primary-b1)"
        >
          {{ amenity.name }}
        </USIconListItem>
      </USList>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineComponent, defineProps, PropType } from 'vue'
import { RouteLocationRaw } from 'vue-router'

import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import USIcon from '@base/components/USIcon/USIcon.vue'
import USIconListItem from '@base/components/USIconListItem/USIconListItem.vue'
import USList from '@base/components/USList/USList.vue'
import USRatingStars from '@base/components/USRatingStars/USRatingStars.vue'

import ImageClubLogoPlaceHolder from '@base/_assets/club-logo-placeholder.jpg'

interface Amenity {
  icon: string
  name: string
}

type ClubAvatar = {
  src?: string
  srcset?: string
  sizes?: string
  alt?: string
  title?: string
}

defineComponent({
  name: 'ClubDetails',
  components: {
    ImageWrapper,
    USIcon,
    USIconListItem,
    USList,
    USRatingStars
  }
})

const props = defineProps({
  heading: { type: String, default: '' },
  clubAvatar: {
    type: Object as PropType<ClubAvatar>,
    required: true
  },
  clubName: { type: String, default: '' },
  clubAddress: { type: String, default: '' },
  clubPhoneNumber: { type: String, default: '' },
  clubRating: { type: Number, required: true },
  amenities: {
    type: Array as PropType<Amenity[]>,
    default: () => []
  },
  to: {
    type: Object as PropType<RouteLocationRaw>,
    required: true
  }
})

const avatarProps = computed(() => {
  let clubAvatar = props.clubAvatar || {}

  if (!clubAvatar || !clubAvatar.src) {
    clubAvatar.src = ImageClubLogoPlaceHolder
    clubAvatar.srcset = ImageClubLogoPlaceHolder
    clubAvatar.alt =
      'The upswing logo as a default placeholder for the club logo'
  }

  return {
    ...clubAvatar,
    height: 'var(--avatar-diameter)',
    aspectRatio: '1',
    fadeIn: true,
    lazy: true,
    objectFit: 'contain',
    width: 'var(--avatar-diameter)'
  }
})
</script>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;

$amenity-icon-diameter-mobile: 26px;
$amenity-icon-diameter-tablet: 36px;
$amenity-icon-diameter-desktop: 44px;

$avatar-diameter-mobile: 60px;
$avatar-diameter-tablet: 73px;
$avatar-diameter-desktop: 80px;

$contact-icon-diameter-mobile: 18px;
$contact-icon-diameter-tablet: 22px;
$contact-icon-diameter-desktop: 26px;

$stars-diameter-mobile: 16px;
$stars-diameter-tablet: 20px;
$stars-diameter-desktop: 22px;

.club-details {
  --amenity-icon-diameter: #{$amenity-icon-diameter-mobile};
  --avatar-diameter: #{$avatar-diameter-mobile};
  --contact-icon-diameter: #{$contact-icon-diameter-mobile};
  --stars-diameter: #{$stars-diameter-mobile};

  @include bp.min-width(bp.$breakpoint--tablet) {
    --amenity-icon-diameter: #{$amenity-icon-diameter-tablet};
    --avatar-diameter: #{$avatar-diameter-tablet};
    --contact-icon-diameter: #{$contact-icon-diameter-tablet};
    --stars-diameter: #{$stars-diameter-tablet};
  }

  @include bp.min-width(bp.$breakpoint--desktop) {
    --amenity-icon-diameter: #{$amenity-icon-diameter-desktop};
    --avatar-diameter: #{$avatar-diameter-desktop};
    --contact-icon-diameter: #{$contact-icon-diameter-desktop};
    --stars-diameter: #{$stars-diameter-desktop};
  }

  &__row-heading {
    margin-bottom: var(--spacing--6);
  }

  &__header {
    align-items: center;
    display: flex;
    flex-flow: row nowrap;
  }

  &__avatar {
    border-radius: 50%;
    box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.29);
    flex-shrink: 0;
    overflow: hidden;
  }

  &__heading-wrapper {
    margin-left: var(--spacing--6);
  }

  &__rating {
    margin-top: var(--spacing--8);
  }

  &__contact {
    align-items: flex-start;
    display: flex;
    flex-flow: row nowrap;
    margin-top: var(--spacing--5);
  }

  &__contact-item {
    display: flex;
    flex-flow: row nowrap;
    width: 50%;

    &--address {
      align-items: flex-start;
    }

    &--phone {
      align-items: center;
      flex: 0 0 auto;
      margin-left: var(--spacing--5);
    }
  }

  &__contact-icon {
    margin-right: var(--spacing--8);
  }

  &__amenities {
    margin-top: var(--spacing--5);

    @include bp.min-width(bp.$breakpoint--7) {
      margin-top: 0;
    }
  }

  &__amenity-list {
    padding: 0;
  }
}

:deep(.image-wrapper__img) {
  border-radius: 50%;
}
</style>
